"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EventInfoSection = EventInfoSection;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/EventInfoSection/index.tsx
// Figma: TypeNewDesign → EventOverviewText + PropertyBlock
//
// 構造:
//   EventOverviewText  — タイトル (28px bold) + 説明 (14px regular)
//   PropertyBlock      — ガラス面の共通部品 (../PropertyBlock) に委譲
//     PropertyBlockRow  — [CircleIcon bg:background.neutral.default / icon:primary.main]
//                          [label 80px固定 neutral.secondary]
//                          [value neutral.primary W3 / isLink なら semantic.info W6]
//                          [任意 subLinkLabel「（会場MAP）」— value と別遷移先]
//                          ※ Figma には右端の矢印 (ChevronRight) は無い
//                          ※ リンクは下線を使わない代わりに「色 + 字重 (W6)」で
//                            タップできることを伝える。リンクでない値は本文と
//                            同じ W3 のまま
//
// 色はすべて theme.palette.v2 (useV2Palette) と定数トークンから取得する。
// ================================================================
var material_1 = require("@mui/material");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
var Icons_1 = require("../Icons");
var PropertyBlock_1 = require("../PropertyBlock");
// ── Icon map ─────────────────────────────────────────────────────
var ICON_MAP = {
    calendar: Icons_1.CalendarIcon,
    clock: Icons_1.ClockIcon,
    location: Icons_1.MapPinIcon,
};
// ── PropertyRow ──────────────────────────────────────────────────
function PropertyRowItem(_a) {
    var variant = _a.variant, label = _a.label, value = _a.value, _b = _a.isLink, isLink = _b === void 0 ? false : _b, _c = _a.height, height = _c === void 0 ? 64 : _c, onClick = _a.onClick, subLinkLabel = _a.subLinkLabel, onClickSubLink = _a.onClickSubLink, _d = _a.isLast, isLast = _d === void 0 ? false : _d;
    var p = (0, theme_1.useV2Palette)();
    var Icon = ICON_MAP[variant];
    var hasSubLink = !!subLinkLabel && !!onClickSubLink;
    // value と副リンクで遷移先が異なるため、副リンクがある行は行全体を
    // ButtonBase にせず (button の入れ子は不正)、value と副リンクを個別の
    // ButtonBase にする。
    var rowIsButton = isLink && !hasSubLink;
    // Figma の開催日は 2 行に折り返されるため 2 行までは折り返し、
    // それを超える分だけ省略する (行の高さは height で固定)。
    var valueNode = ((0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
            fontSize: tokens_1.fontSize.sm,
            // リンクは下線を使わない代わりに色 + 字重でタップ可能を示す
            fontWeight: isLink ? tokens_1.fontWeight.semiBold : tokens_1.fontWeight.regular,
            lineHeight: "".concat(tokens_1.lineHeight.sm, "px"),
            color: isLink
                ? p.foreground.semantic.info
                : p.foreground.neutral.primary,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            wordBreak: 'break-all',
        }, children: value }));
    return ((0, jsx_runtime_1.jsx)(PropertyBlock_1.PropertyBlockRow, { icon: (0, jsx_runtime_1.jsx)(Icon, {}), label: label, labelVariant: "body", height: height, onClick: rowIsButton ? onClick : undefined, isLast: isLast, children: hasSubLink ? ((0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", alignItems: "center", spacing: "".concat(tokens_1.spacing.sm, "px"), sx: { minWidth: 0 }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { component: isLink ? material_1.ButtonBase : 'div', onClick: isLink ? onClick : undefined, sx: {
                        flex: '1 1 0%',
                        minWidth: 0,
                        display: 'block',
                        textAlign: 'left',
                        cursor: isLink ? 'pointer' : undefined,
                    }, children: valueNode }), (0, jsx_runtime_1.jsxs)(material_1.Typography, { component: material_1.ButtonBase, onClick: onClickSubLink, sx: {
                        flexShrink: 0,
                        fontSize: tokens_1.fontSize.sm,
                        fontWeight: tokens_1.fontWeight.regular,
                        lineHeight: "".concat(tokens_1.lineHeight.sm, "px"),
                        color: p.foreground.neutral.primary,
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                    }, children: ["\uFF08", (0, jsx_runtime_1.jsx)(material_1.Box, { component: "span", sx: {
                                color: p.foreground.semantic.info,
                                // value と同じ理由で、リンク文字だけ W6 にする (括弧は W3)
                                fontWeight: tokens_1.fontWeight.semiBold,
                            }, children: subLinkLabel }), "\uFF09"] })] })) : (valueNode) }));
}
// ── EventInfoSection ─────────────────────────────────────────────
function EventInfoSection(_a) {
    var title = _a.title, description = _a.description, rows = _a.rows;
    var p = (0, theme_1.useV2Palette)();
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { children: [(0, jsx_runtime_1.jsxs)(material_1.Box, { sx: { mb: "".concat(tokens_1.spacing.md, "px") }, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                            fontSize: tokens_1.fontSize.xl,
                            fontWeight: tokens_1.fontWeight.bold,
                            lineHeight: "".concat(tokens_1.lineHeight.xl, "px"),
                            letterSpacing: '-0.1px',
                            color: p.foreground.neutral.primary,
                            mb: description ? "".concat(tokens_1.spacing.sm, "px") : 0,
                        }, children: title }), description && ((0, jsx_runtime_1.jsx)(material_1.Typography, { component: "div", sx: {
                            fontSize: tokens_1.fontSize.sm,
                            fontWeight: tokens_1.fontWeight.regular,
                            lineHeight: "".concat(tokens_1.lineHeight.sm, "px"),
                            color: p.foreground.neutral.primary,
                            // 長い URL でカードを突き破らないように (v1 と同じ)
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                        }, children: description }))] }), (0, jsx_runtime_1.jsx)(PropertyBlock_1.PropertyBlock, { children: rows.map(function (row, i) { return ((0, jsx_runtime_1.jsx)(PropertyRowItem, __assign({}, row, { isLast: i === rows.length - 1 }), "".concat(row.variant, "-").concat(i))); }) })] }));
}
